'use client'

import { useState, type PropsWithChildren, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
// eslint-disable-next-line no-restricted-imports
import { WLink, WLinkButton } from '@alice-health/wonderland-react'
/*
 * `import type` e obrigatorio aqui: importar valores de
 * @alice-health/wonderland puxa o bundle do Stencil para o SSR e quebra com
 * "HTMLElement is not defined". Estes dois sao apenas interfaces.
 */
import type {
  WIconComponentProps,
  WLinkButtonComponentProps
} from '@alice-health/wonderland'

import type {
  ClassName,
  Wvariant,
  Wsize,
  WLinkVariant,
  WLinkSize
} from '@/global/types'
import { makeUrlWithUtms } from '@/global/utils/query-params'
import { DispatchEventProperties } from '@/global/utils/analytics-events/types'
import { dispatchEvent } from '@/global/utils/analytics-events'
import { LinkWrapper } from './LinkWrapper'

export interface AppLinkBaseProps {
  id?: string
  href: string
  variant?: WLinkVariant | Wvariant
  quiet?: boolean
  iconButton?: boolean
  icon?: WIconComponentProps['icon']
  iconPosition?: WLinkButtonComponentProps['iconPosition']
  as: 'link' | 'button'
  target?: '_self' | '_parent' | '_blank' | '_top'
  className?: ClassName
  withoutUtms?: boolean
  block?: boolean
  hash?: string
  query?: URLSearchParams
  onClick?: () => void
  event?: DispatchEventProperties
  /*
   * Atributos da ancora, nao do componente do design system: o `WLink` e o
   * `WLinkButton` ficam dentro do elemento clicavel, entao e a ancora que
   * carrega o papel de disclosure e a ordem de tabulacao.
   */
  ariaHasPopup?: boolean
  ariaExpanded?: boolean
  ariaControls?: string
  ariaLabel?: string
  tabIndex?: number
}
export interface WAppLinkBaseProps extends AppLinkBaseProps {
  as: 'link'
  variant?: WLinkVariant
  size?: WLinkSize
  quiet?: boolean
  iconButton?: undefined
  iconPosition?: undefined
}

export interface WAppLinkButtonBaseProps extends AppLinkBaseProps {
  as: 'button'
  variant?: Wvariant
  size?: Wsize
  quiet?: undefined
  iconButton?: boolean
  iconPosition?: WLinkButtonComponentProps['iconPosition']
}

export type AppLinkProps = PropsWithChildren<
  WAppLinkBaseProps | WAppLinkButtonBaseProps
>
export const AppLink = ({
  id,
  as,
  variant,
  size,
  iconButton = false,
  children,
  href,
  withoutUtms = false,
  hash,
  query,
  onClick,
  className,
  event,
  ariaHasPopup,
  ariaExpanded,
  ariaControls,
  ariaLabel,
  tabIndex,
  ...linkProps
}: AppLinkProps) => {
  const searchParams = useSearchParams()
  /**
   * use setMounted for avoid 'Uncaught Error: Minified React error #418'
   * Ref: https://legacy.reactjs.org/docs/error-decoder.html/?invariant=418
   */
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // return this null to avoid hydration errors
  if (!mounted) {
    return null
  }

  const url = () => {
    let url = ''

    if (withoutUtms) {
      url = query ? `${href}?${query.toString()}` : href
    } else {
      url = makeUrlWithUtms({ href, query, searchParams })
    }

    url = hash ? `${url}#${hash}` : url
    return url
  }

  const handleClick = () => {
    if (event) dispatchEvent(event)
    onClick?.()
  }

  if (as === 'button') {
    return (
      <LinkWrapper
        id={id}
        data-testid={id}
        href={url()}
        target={linkProps.target}
        className={className}
        onClick={handleClick}
        ariaHasPopup={ariaHasPopup}
        ariaExpanded={ariaExpanded}
        ariaControls={ariaControls}
        ariaLabel={ariaLabel}
        tabIndex={tabIndex}
      >
        <WLinkButton
          {...linkProps}
          variant={variant}
          size={size}
          iconButton={iconButton}
          icon-position={linkProps.iconPosition}
          block={linkProps.block}
        >
          {children}
        </WLinkButton>
      </LinkWrapper>
    )
  }

  return (
    <LinkWrapper
      id={id}
      data-testid={id}
      href={url()}
      target={linkProps.target}
      className={className}
      onClick={handleClick}
      ariaHasPopup={ariaHasPopup}
      ariaExpanded={ariaExpanded}
      ariaControls={ariaControls}
      ariaLabel={ariaLabel}
      tabIndex={tabIndex}
    >
      {/*
       * O `LinkWrapper` (o `Link` do Next, quando a rota é interna) repassa o
       * `href` para o filho. Isso exigia um `@ts-expect-error` aqui até o major
       * 8 do design system; o wrapper do major 9 declara os tipos do `WLink`
       * explicitamente e a supressão virou desnecessária — se o typecheck voltar
       * a reclamar de `href` neste ponto, é aqui que a história está.
       */}
      <WLink
        data-testid={`${id}s`}
        {...linkProps}
        variant={variant}
        /*
         * A union de props ja garante que `size` aqui e um tamanho de WLink: o
         * ramo de `as='button'` retornou acima. Quem perde o estreitamento e a
         * desestruturacao na assinatura, nao o tipo publico.
         */
        size={size as WLinkSize | undefined}
      >
        {children}
      </WLink>
    </LinkWrapper>
  )
}
